import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";

const links = [
  { hash: "services", label: "Services" },
  { hash: "how", label: "How It Works" },
  { hash: "partners", label: "Partner Network" },
  { hash: "about", label: "About" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Smooth-scroll to a hash on the homepage after navigation completes
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    // wait a tick for the route to render
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 50);
    return () => clearTimeout(t);
  }, [location.pathname, location.hash]);

  const goToSection = (hash: string) => {
    setOpen(false);
    if (isHome) {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${hash}`);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-ink/95 shadow-elevated backdrop-blur-md" : "bg-gradient-to-b from-ink/80 to-transparent"
      }`}
    >
      <nav className="container flex h-20 items-center justify-between">
        <Link to="/" onClick={goHome} aria-label="Garage Kings home">
          <Logo light />
        </Link>
        <div className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <button
              key={l.hash}
              onClick={() => goToSection(l.hash)}
              className="text-sm font-semibold uppercase tracking-wide text-white/80 transition-colors hover:text-gold"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/epoxy-flooring"
            onClick={() => setOpen(false)}
            className="text-sm font-semibold uppercase tracking-wide text-white/80 transition-colors hover:text-gold"
          >
            Epoxy Flooring
          </Link>
        </div>
        <Button
          asChild
          className="hidden bg-gold font-bold uppercase tracking-wider text-ink hover:bg-gold/90 lg:inline-flex"
        >
          <a href="https://api.whatsapp.com/send?phone=15874073024&text=Hi%20Garage%20Kings!%20%F0%9F%91%91%20I%27d%20like%20a%20quote%20for%20a%20cleanout.%20What%20photos%20or%20info%20do%20you%20need%20from%20me%20to%20get%20started%3F" target="_blank" rel="noopener noreferrer">
            Get Your Fast Estimate
          </a>
        </Button>
        <button
          aria-label="Menu"
          onClick={() => setOpen((v) => !v)}
          className="rounded-md p-2 text-white lg:hidden"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>
      {open && (
        <div className="border-t border-white/10 bg-ink lg:hidden">
          <div className="container flex flex-col gap-1 py-4">
            {links.map((l) => (
              <button
                key={l.hash}
                onClick={() => goToSection(l.hash)}
                className="rounded-md px-4 py-3 text-left font-semibold uppercase tracking-wide text-white hover:bg-white/5"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/epoxy-flooring"
              onClick={() => setOpen(false)}
              className="rounded-md px-4 py-3 text-left font-semibold uppercase tracking-wide text-white hover:bg-white/5"
            >
              Epoxy Flooring
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
